import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  WarningOutlined,
  IdcardOutlined,
  ClockCircleOutlined,
  ExceptionOutlined,
  SolutionOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import {
  getHrmsFollowUpQueue,
  type HrmsAgingBucket,
  type HrmsDashboardActionSummary,
  type HrmsFollowUpMode,
  type HrmsFollowUpRow,
} from '@/services/hrmsDashboard.service';

const { Text } = Typography;

const agingColor: Record<HrmsAgingBucket, string> = {
  '0-7': 'green',
  '8-14': 'gold',
  '15-30': 'orange',
  '30+': 'red',
};

export const HrmsFollowUpZone: React.FC<{
  summary: HrmsDashboardActionSummary;
  asOnDate?: string;
  initialMode?: HrmsFollowUpMode;
}> = ({ summary, asOnDate, initialMode = 'expired_docs' }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<HrmsFollowUpMode>(initialMode);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [aging, setAging] = useState<HrmsAgingBucket | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    setMode(initialMode);
    setPage(1);
  }, [initialMode]);

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['hrms-follow-up-queue', mode, page, pageSize, aging, searchApplied, asOnDate],
    queryFn: () =>
      getHrmsFollowUpQueue({
        mode,
        page,
        pageSize,
        aging_bucket: aging,
        search: searchApplied || undefined,
        as_on_date: asOnDate,
      }),
    placeholderData: (prev) => prev,
  });

  const agingCounts = data?.aging;
  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];

  const columns: ColumnsType<HrmsFollowUpRow> = [
    {
      title: 'Item',
      dataIndex: 'title',
      key: 'title',
      width: 180,
      ellipsis: true,
      render: (v: string, r) => <a onClick={() => navigate(r.link_path)}>{v}</a>,
    },
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: 160,
      ellipsis: true,
      render: (v?: string, r?: HrmsFollowUpRow) =>
        v || r?.employee_code ? (
          <span>
            {v || '—'}
            {r?.employee_code ? (
              <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                {r.employee_code}
              </Text>
            ) : null}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Detail',
      dataIndex: 'subtitle',
      key: 'subtitle',
      ellipsis: true,
      render: (v?: string) => v || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v?: string) => (v ? <Tag>{v}</Tag> : '—'),
    },
    {
      title: 'Date',
      dataIndex: 'ref_date',
      key: 'ref_date',
      width: 110,
      render: (v?: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    {
      title: 'Aging',
      dataIndex: 'aging_days',
      key: 'aging_days',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.aging_days - b.aging_days,
      render: (d: number, r) => (
        <Tag color={agingColor[r.aging_bucket]} style={{ marginInlineEnd: 0 }}>
          {d}d · {r.aging_bucket}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          Open
        </Button>
      ),
    },
  ];

  const tableBody = isError ? (
    <Alert
      type="error"
      showIcon
      message={(error as Error)?.message ?? 'Failed to load HR follow-up queue'}
    />
  ) : isFetching && !data ? (
    <Spin />
  ) : rows.length === 0 ? (
    <EmptyQueueState message="No items in this queue for the selected filters." />
  ) : (
    <Table
      rowKey="id"
      size="small"
      tableLayout="fixed"
      loading={isFetching}
      dataSource={rows}
      columns={columns}
      scroll={{ x: 900 }}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (t, range) => `${range[0]}–${range[1]} of ${t}`,
        onChange: (nextPage, nextSize) => {
          setPage(nextPage);
          if (nextSize && nextSize !== pageSize) {
            setPageSize(nextSize);
            setPage(1);
          }
        },
      }}
    />
  );

  const setModeAndReset = (m: HrmsFollowUpMode) => {
    setMode(m);
    setPage(1);
  };

  const overdueOnb = summary.talent?.overdue_onboarding_tasks ?? 0;

  return (
    <ControlTowerSectionCard
      zone="overdue"
      sectionId="hrms-follow-up"
      title={
        <Space>
          <WarningOutlined />
          <span>
            Compliance & people follow-up ({summary.expired_docs_count} expired ·{' '}
            {summary.pending_leave_count} leave · {summary.confirmation_due_count} confirm)
          </span>
        </Space>
      }
      extra={
        <Space>
          <Button type="link" size="small" onClick={() => navigate('/hr/document-expiry')}>
            Document register
          </Button>
          <Button type="link" size="small" onClick={() => navigate('/hr/leaves')}>
            Leave register
          </Button>
        </Space>
      }
    >
      <ControlTowerMetricStrip zone="metricsPayables" title="Priority queues">
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Expired docs"
            count={summary.expired_docs_count}
            icon={<IdcardOutlined />}
            urgency={summary.expired_docs_count > 0 ? 'critical' : 'neutral'}
            footerLink={{ label: 'Follow up', onClick: () => setModeAndReset('expired_docs') }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Docs due soon"
            count={summary.docs_due_soon_count}
            icon={<IdcardOutlined />}
            urgency={summary.docs_due_soon_count > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Follow up', onClick: () => setModeAndReset('docs_due_soon') }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Pending leave"
            count={summary.pending_leave_count}
            icon={<ClockCircleOutlined />}
            urgency={summary.pending_leave_count > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Follow up', onClick: () => setModeAndReset('pending_leave') }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Confirmation due"
            count={summary.confirmation_due_count}
            icon={<ExceptionOutlined />}
            urgency={summary.confirmation_due_count > 0 ? 'attention' : 'neutral'}
            footerLink={{ label: 'Follow up', onClick: () => setModeAndReset('confirmation_due') }}
          />
        </Col>
      </ControlTowerMetricStrip>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12, marginBottom: 12 }}
        message="Full follow-up queue (not capped at 15). Age filters apply to the active tab."
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="Search employee / item / status"
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => {
            setSearchApplied(v.trim());
            setPage(1);
          }}
        />
        <Select
          allowClear
          placeholder="Aging"
          style={{ width: 140 }}
          value={aging}
          onChange={(v) => {
            setAging(v);
            setPage(1);
          }}
          options={[
            { value: '0-7', label: `0–7 (${agingCounts?.['0-7'] ?? 0})` },
            { value: '8-14', label: `8–14 (${agingCounts?.['8-14'] ?? 0})` },
            { value: '15-30', label: `15–30 (${agingCounts?.['15-30'] ?? 0})` },
            { value: '30+', label: `30+ (${agingCounts?.['30+'] ?? 0})` },
          ]}
        />
        <Button size="small" onClick={() => refetch()} loading={isFetching}>
          Refresh queue
        </Button>
        <Text type="secondary">
          {total} item{total === 1 ? '' : 's'}
        </Text>
      </Space>

      <Tabs
        size="small"
        activeKey={mode}
        onChange={(k) => setModeAndReset(k as HrmsFollowUpMode)}
        items={[
          {
            key: 'expired_docs',
            label: `Expired docs (${summary.expired_docs_count})`,
            children: mode === 'expired_docs' ? tableBody : null,
          },
          {
            key: 'docs_due_soon',
            label: `Docs due soon (${summary.docs_due_soon_count})`,
            children: mode === 'docs_due_soon' ? tableBody : null,
          },
          {
            key: 'contracts_ending',
            label: (
              <span>
                <SolutionOutlined /> Contracts ({summary.contracts_ending_count})
              </span>
            ),
            children: mode === 'contracts_ending' ? tableBody : null,
          },
          {
            key: 'pending_leave',
            label: `Leave (${summary.pending_leave_count})`,
            children: mode === 'pending_leave' ? tableBody : null,
          },
          {
            key: 'pending_ot',
            label: `OT (${summary.pending_ot_count})`,
            children: mode === 'pending_ot' ? tableBody : null,
          },
          {
            key: 'probation_ending',
            label: `Probation 30d (${summary.probation_ending_count})`,
            children: mode === 'probation_ending' ? tableBody : null,
          },
          {
            key: 'confirmation_due',
            label: `Confirmation (${summary.confirmation_due_count})`,
            children: mode === 'confirmation_due' ? tableBody : null,
          },
          ...(summary.talent?.enabled
            ? [
                {
                  key: 'onboarding_overdue' as const,
                  label: (
                    <span>
                      <TeamOutlined /> Onboarding ({overdueOnb})
                    </span>
                  ),
                  children: mode === 'onboarding_overdue' ? tableBody : null,
                },
              ]
            : []),
        ]}
      />
    </ControlTowerSectionCard>
  );
};

export default HrmsFollowUpZone;
